import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { isInternalPath, localizedPath } from '../../i18n/path';

type Variant = 'primary' | 'secondary' | 'ghost' | 'accent';
type Size = 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-secondary-500 text-white hover:bg-secondary-600 active:bg-secondary-700',
  secondary:
    'bg-white/70 text-primary-500 ring-1 ring-inset ring-slate-200/70 backdrop-blur-md backdrop-saturate-150 hover:bg-white/90 supports-[backdrop-filter]:bg-white/55 motion-reduce:bg-white motion-reduce:backdrop-blur-none',
  ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-primary-600',
  accent: 'bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700',
};

const SIZES: Record<Size, string> = {
  md: 'min-h-11 px-6 py-2.5 text-sm tracking-[-0.01em]',
  lg: 'min-h-14 px-10 py-3.5 text-base tracking-[-0.01em]',
};

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold ' +
  'transition-[color,background-color,box-shadow,transform] duration-100 ease-out ' +
  'active:scale-[0.97] motion-reduce:active:scale-100 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100';
interface StyleProps {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  className?: string;
}

function buttonClasses({ variant = 'primary', size = 'md', fullWidth, className = '' }: StyleProps) {
  return [BASE, VARIANTS[variant], SIZES[size], fullWidth ? 'w-full' : '', className]
    .filter(Boolean)
    .join(' ');
}

export type ButtonProps = StyleProps & ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode };

export function Button({ variant, size, fullWidth, className, children, ...props }: ButtonProps) {
  return (
    <button className={buttonClasses({ variant, size, fullWidth, className })} {...props}>
      {children}
    </button>
  );
}

export type ButtonLinkProps = StyleProps & {
  to: string;
  ariaLabel?: string;
  children: ReactNode;
};

/** Same visual treatment as Button, rendered as a normal navigation link. */
export function ButtonLink({
  to,
  ariaLabel,
  variant,
  size,
  fullWidth,
  className,
  children,
}: ButtonLinkProps) {
  return (
    <a href={isInternalPath(to) ? localizedPath(to) : to} aria-label={ariaLabel} className={buttonClasses({ variant, size, fullWidth, className })}>
      {children}
    </a>
  );
}
